/**
 * Portfolio Details Page - View and manage portfolio metadata.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { portfoliosApi, portfolioApi } from '../services/api';
import type { PortfolioMetadata, PortfolioResponse } from '../types';

export function PortfolioDetailsPage() {
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const [portfolio, setPortfolio] = useState<PortfolioMetadata | null>(null);
  const [portfolioState, setPortfolioState] = useState<PortfolioResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (portfolioId) {
      loadPortfolioData();
    }
  }, [portfolioId]);

  const loadPortfolioData = async () => {
    if (!portfolioId) return;

    try {
      setLoading(true);
      setError(null);

      const [metadataResponse, stateResponse] = await Promise.all([
        portfoliosApi.get(portfolioId),
        portfolioApi.getCurrentById(portfolioId).catch(() => null), // Gracefully handle if no state yet
      ]);

      setPortfolio(metadataResponse.portfolio);
      setPortfolioState(stateResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolio');
      console.error('Portfolio load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !portfolio) {
    return <Alert severity="error">{error || 'Portfolio not found'}</Alert>;
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h4" gutterBottom>
                {portfolio.name}
              </Typography>
              {portfolio.description && (
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  {portfolio.description}
                </Typography>
              )}
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip
                title={
                  portfolio.portfolioType === 'LIVE'
                    ? 'LIVE portfolios represent real capital backed by actual brokerage accounts. Recommendations apply to real money, but trades are executed manually.'
                    : 'SANDBOX portfolios use virtual capital for testing and research. Perfect for exploring strategies without risking real money.'
                }
                arrow
              >
                <Chip
                  label={portfolio.portfolioType}
                  color={portfolio.portfolioType === 'LIVE' ? 'primary' : 'default'}
                />
              </Tooltip>
              {portfolio.isPrimary && <Chip label="Primary" color="secondary" />}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Portfolio Information */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Portfolio Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Portfolio ID
                  </Typography>
                  <Typography variant="body1">{portfolio.portfolioId}</Typography>
                </Box>
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Type
                    </Typography>
                    <Tooltip
                      title={
                        portfolio.portfolioType === 'LIVE'
                          ? 'LIVE portfolios represent real capital backed by actual brokerage accounts. Recommendations apply to real money, but trades are executed manually.'
                          : 'SANDBOX portfolios use virtual capital for testing and research. Perfect for exploring strategies without risking real money.'
                      }
                      arrow
                    >
                      <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
                        <InfoIcon fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="body1">{portfolio.portfolioType}</Typography>
                </Box>
                {portfolio.executionMode && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Execution Mode
                    </Typography>
                    <Typography variant="body1">{portfolio.executionMode}</Typography>
                  </Box>
                )}
                {portfolio.thesis && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Thesis
                    </Typography>
                    <Typography variant="body1">{portfolio.thesis}</Typography>
                  </Box>
                )}
                {portfolio.riskProfile && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Risk Profile
                    </Typography>
                    <Typography variant="body1">{portfolio.riskProfile}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Email Settings */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Email Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email Enabled
                  </Typography>
                  <Typography variant="body1">
                    {portfolio.emailEnabled !== false ? 'Yes' : 'No'}
                  </Typography>
                </Box>
                {portfolio.emailFrequency && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email Frequency
                    </Typography>
                    <Typography variant="body1">{portfolio.emailFrequency}</Typography>
                  </Box>
                )}
                {portfolio.weeklyEmailDay && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Weekly Email Day
                    </Typography>
                    <Typography variant="body1">{portfolio.weeklyEmailDay}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Portfolio State */}
      {portfolioState && (
        <Box sx={{ mt: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current State
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Total Value
                      </Typography>
                      <Typography variant="h5">
                        ${portfolioState.portfolio.total_value.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Paper>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Cash Balance
                      </Typography>
                      <Typography variant="h5">
                        ${portfolioState.portfolio.cash_balance.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Paper>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Positions
                      </Typography>
                      <Typography variant="h5">{portfolioState.positions.length}</Typography>
                    </Paper>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

      {/* Metadata */}
      <Box sx={{ mt: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Metadata
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(portfolio.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Updated: {new Date(portfolio.updatedAt).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
    </Box>
  );
}

